import React from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Checkbox,
  Stack,
} from "@mui/material";

const CommonTableCardView = ({
  columns = [],
  data = [],
  maxHeight = "100vh",
  multiSelect = false,
  selectAll = false,
  handleSelectAll = () => {},
  handleRowSelection = () => {},
  disableSelectionIf = null,
  cardSx = {},
}) => {
  // Separate columns into regular and action columns
  const regularColumns = [];
  const actionColumn = columns.find(
    (col) =>
      col.header === "Action" ||
      col.header === "Actions" ||
      col.accessorKey === "action"
  );

  columns.forEach((column) => {
    // Skip serial number columns
    if (
      column.accessorKey === "#" ||
      column.header === "#" ||
      column.header === "Sr.No"
    )
      return;
    // Skip action columns
    if (
      column.header === "Action" ||
      column.header === "Actions" ||
      column.accessorKey === "action"
    )
      return;
    regularColumns.push(column);
  });

  return (
    <Box>
      {/* Header with Select All if multiSelect is enabled */}
      {multiSelect && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            // mb: 2,
            px: 1,
            py: 1,
            backgroundColor: "#f8f9fa",
            borderRadius: "8px",
            border: "1px solid #e0e0e0",
          }}
        >
          <Stack direction="row" alignItems="center" gap={1}>
            <Typography variant="button" color="primary">
              Select All
            </Typography>
            <Checkbox
              checked={selectAll}
              onChange={(e) => handleSelectAll(e.target.checked)}
              size="small"
            />
          </Stack>
          <Typography sx={{ fontSize: "12px", color: "#666" }}>
            {data.length} items
          </Typography>
        </Box>
      )}

      {/* Scrollable Card Container */}
      <Box
        sx={{
          maxHeight: maxHeight,
          overflowY: "auto",
          "&::-webkit-scrollbar": {
            width: 6,
          },
          "&::-webkit-scrollbar-track": {
            backgroundColor: "transparent",
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "#ccc",
            borderRadius: "10px",
            "&:hover": {
              backgroundColor: "#999",
            },
          },
        }}
      >
        {data.length === 0 ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              minHeight: "50vh",
              gap: 2,
            }}
          >
            <Typography sx={{ color: "#999", fontSize: "14px" }}>
              No data available
            </Typography>
          </Box>
        ) : (
          data.map((item, index) => {
            const isDisabled = disableSelectionIf
              ? disableSelectionIf(item)
              : false;
            const isChecked = item?.checked || false;

            return (
              <Card
                key={index}
                sx={{
                  mb: 1.5,
                  borderRadius: "12px",
                  border: "1px solid #e0e0e0",
                  boxShadow: "none",
                  overflow: "hidden",
                  backgroundColor: "#fff",
                  ...cardSx,
                }}
              >
                <CardContent sx={{ p: 1, "&:last-child": { pb: 1 } }}>
                  {/* Header with checkbox if multiSelect */}
                  {multiSelect && !isDisabled && (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        mb: 1,
                        pb: 1,
                        borderBottom: "0.5px solid #f0f0f0",
                      }}
                    >
                      <Checkbox
                        checked={isChecked}
                        onChange={(e) => {
                          handleRowSelection(String(index), e.target.checked);
                        }}
                        size="small"
                      />
                      <Typography variant="caption" sx={{ color: "#666" }}>
                        #{index + 1 < 10 ? `0${index + 1}` : index + 1}
                      </Typography>
                    </Box>
                  )}

                  {/* Grid layout for fields - 3 columns */}
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "repeat(3, 1fr)",
                      gap: 1,
                      mb: actionColumn ? 1.5 : 0,
                    }}
                  >
                    {regularColumns.map((column, colIndex) => {
                      let value;
                      let displayValue;

                      // Use custom Cell renderer if it exists
                      if (column.Cell) {
                        displayValue = column.Cell({
                          cell: {
                            row: {
                              original: item,
                              id: index,
                            },
                          },
                          row: {
                            original: item,
                            id: index,
                          },
                        });
                      } else if (column.accessorFn) {
                        value = column.accessorFn(item);
                        displayValue = value;
                      } else if (column.accessorKey) {
                        const keys = column.accessorKey.split(".");
                        value = keys.reduce((obj, key) => obj?.[key], item);
                        displayValue = value;
                      }

                      return (
                        <Box key={colIndex}>
                          <Typography variant="button" color="primary">
                            {column.header}
                          </Typography>
                          <Box
                            sx={{
                              fontSize: "12px",
                              color: "#000",
                              fontWeight: 600,
                              "& a": {
                                color: "#2789FD",
                                textDecoration: "none",
                                fontWeight: 500,
                              },
                              "& .MuiTypography-root": {
                                fontSize: "12px",
                                fontWeight: 500,
                              },
                            }}
                          >
                            {displayValue ?? "--"}
                          </Box>
                        </Box>
                      );
                    })}
                  </Box>

                  {/* Action buttons at bottom if exists */}
                  {actionColumn && (
                    <Box
                      sx={{
                        pt: 1.5,
                        borderTop: "0.5px solid #f0f0f0",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                      }}
                    >
                      {actionColumn.Cell({
                        cell: {
                          row: {
                            original: item,
                            id: index,
                          },
                        },
                        row: {
                          original: item,
                          id: index,
                        },
                      })}
                    </Box>
                  )}
                </CardContent>
              </Card>
            );
          })
        )}
      </Box>
    </Box>
  );
};

export default CommonTableCardView;
